import * as THREE from 'three'
import { FontLoader } from 'three/addons/loaders/FontLoader.js'
import { TextGeometry } from 'three/addons/geometries/TextGeometry.js'
import helvetikerBoldFont from 'three/examples/fonts/helvetiker_bold.typeface.json'

const SWAG_LETTERS = 'SWAG'

export function createSwagSpiral({ scene, params }) {
  const spiralMaterial = new THREE.LineBasicMaterial({
    color: 0xff5a1f,
    transparent: true,
    opacity: 0.95
  })
  const swagFont = new FontLoader().parse(helvetikerBoldFont)
  const swagLetterGeometries = createSwagLetterGeometries(swagFont)
  const swagTextMaterial = new THREE.MeshBasicMaterial({
    color: 0x39ff14,
    toneMapped: false,
    side: THREE.DoubleSide
  })

  let spiralLine = null
  let modelBounds = null
  let swagWords = []
  let swagTravel = 0

  function setModelBounds(bounds) {
    modelBounds = {
      center: bounds.center.clone(),
      size: bounds.size.clone()
    }
    updateLine()
  }

  function interpolateWidth(t) {
    if (t < 0.5) {
      return THREE.MathUtils.lerp(params.startWidth, params.middleWidth, t * 2)
    }

    return THREE.MathUtils.lerp(params.middleWidth, params.endWidth, (t - 0.5) * 2)
  }

  function getPoint(t, target = new THREE.Vector3()) {
    if (!modelBounds) {
      return target.set(0, 0, 0)
    }

    const { center, size } = modelBounds
    const turns = 3.4
    const normalizedT = THREE.MathUtils.euclideanModulo(t, 1)
    const angle = normalizedT * turns * Math.PI * 2
    const height = Math.max(size.y, 0.01) * 1.08
    const baseRadius = Math.max(size.x, size.z, 0.01) * 0.5
    const radius = baseRadius * Math.max(interpolateWidth(normalizedT), 0.001)

    return target.set(
      center.x + Math.cos(angle) * radius,
      center.y - height * 0.5 + height * normalizedT,
      center.z + Math.sin(angle) * radius
    )
  }

  function getTangent(t, target = new THREE.Vector3()) {
    const normalizedT = THREE.MathUtils.euclideanModulo(t, 1)
    const tangentStep = 0.001
    const beforeT = Math.max(0, normalizedT - tangentStep)
    const afterT = Math.min(1, normalizedT + tangentStep)
    const before = getPoint(beforeT, new THREE.Vector3())
    const after = getPoint(afterT, new THREE.Vector3())
    return target.subVectors(after, before).normalize()
  }

  function getApproxLength() {
    const samples = 120
    let length = 0
    const previousPoint = getPoint(0, new THREE.Vector3())
    const point = new THREE.Vector3()

    for (let i = 1; i <= samples; i += 1) {
      getPoint(i / samples, point)
      length += previousPoint.distanceTo(point)
      previousPoint.copy(point)
    }

    return Math.max(length, 0.001)
  }

  function getSwagCenterInfluence(t) {
    const centerDistance = Math.abs(THREE.MathUtils.euclideanModulo(t, 1) - 0.5)
    const normalizedDistance = centerDistance / Math.max(params.swagCenterRange, 0.001)

    return 1 - THREE.MathUtils.smoothstep(normalizedDistance, 0, 1)
  }

  function getSwagScale(t) {
    const centerInfluence = getSwagCenterInfluence(t)
    const centerSize = params.swagSize * (1 + params.swagCenterSizeBoost)

    return THREE.MathUtils.lerp(params.swagSmallTargetSize, centerSize, centerInfluence)
  }

  function getSwagLetterGap(t) {
    return THREE.MathUtils.lerp(params.swagLetterGap, params.swagCenterLetterGap, getSwagCenterInfluence(t))
  }

  function syncTexts() {
    const quantity = Math.max(0, Math.round(params.swagQuantity))

    while (swagWords.length < quantity) {
      const word = [...SWAG_LETTERS].map((letter) => {
        const text = new THREE.Mesh(swagLetterGeometries[letter], swagTextMaterial)
        scene.add(text)
        return { letter, text }
      })
      swagWords.push(word)
    }

    while (swagWords.length > quantity) {
      const word = swagWords.pop()
      for (const { text } of word) {
        scene.remove(text)
      }
    }
  }

  function updateLine() {
    if (!modelBounds) {
      return
    }

    const segments = 420
    const positions = new Float32Array((segments + 1) * 3)
    const point = new THREE.Vector3()

    for (let i = 0; i <= segments; i += 1) {
      const t = i / segments
      getPoint(t, point)
      const offset = i * 3

      positions[offset] = point.x
      positions[offset + 1] = point.y
      positions[offset + 2] = point.z
    }

    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))

    if (!spiralLine) {
      spiralLine = new THREE.Line(geometry, spiralMaterial)
      scene.add(spiralLine)
    } else {
      spiralLine.geometry.dispose()
      spiralLine.geometry = geometry
    }

    spiralLine.visible = params.enabled && params.showLine
    syncTexts()
    update(0)
  }

  function update(deltaTime = 0) {
    if (!modelBounds) {
      return
    }

    swagTravel += deltaTime * params.swagSpeed
    const quantity = Math.max(1, swagWords.length)
    const pathLength = getApproxLength()
    const xAxis = new THREE.Vector3()
    const yAxis = new THREE.Vector3()
    const zAxis = new THREE.Vector3()
    const worldUp = new THREE.Vector3(0, 1, 0)
    const rotationMatrix = new THREE.Matrix4()
    const travelDirection = params.swagSpeed < 0 ? -1 : 1

    for (let i = 0; i < swagWords.length; i += 1) {
      const word = swagWords[i]
      const baseT = THREE.MathUtils.euclideanModulo(swagTravel + i / quantity, 1)
      const baseSize = getSwagScale(baseT)
      const letterGap = getSwagLetterGap(baseT)
      const letterWidths = word.map(({ letter }) => swagLetterGeometries[letter].userData.width)
      const wordWidth = letterWidths.reduce((sum, width) => sum + width, 0) + letterGap * (word.length - 1)
      let cursorX = -wordWidth * 0.5

      for (let letterIndex = 0; letterIndex < word.length; letterIndex += 1) {
        const { text } = word[letterIndex]
        const offsetX = cursorX + letterWidths[letterIndex] * 0.5
        const t = THREE.MathUtils.euclideanModulo(baseT + (offsetX * baseSize * travelDirection) / pathLength, 1)
        const size = getSwagScale(t)

        getPoint(t, text.position)
        getTangent(t, xAxis)
        xAxis.multiplyScalar(travelDirection)
        yAxis.copy(worldUp).addScaledVector(xAxis, -worldUp.dot(xAxis))
        if (yAxis.lengthSq() < 0.0001) {
          yAxis.set(0, 0, 1).addScaledVector(xAxis, -xAxis.z)
        }
        yAxis.normalize()
        zAxis.crossVectors(xAxis, yAxis).normalize()
        rotationMatrix.makeBasis(xAxis, yAxis, zAxis)
        text.quaternion.setFromRotationMatrix(rotationMatrix)
        text.scale.setScalar(size)
        text.visible = params.enabled
        cursorX += letterWidths[letterIndex] + letterGap
      }
    }
  }

  function getProgress() {
    return THREE.MathUtils.euclideanModulo(swagTravel, 1)
  }

  function setProgress(progress) {
    swagTravel = THREE.MathUtils.euclideanModulo(progress, 1)
    update(0)
  }

  return {
    setModelBounds,
    updateLine,
    update,
    getProgress,
    setProgress
  }
}

function createSwagLetterGeometries(font) {
  return [...new Set(SWAG_LETTERS)].reduce((geometries, letter) => {
    const geometry = new TextGeometry(letter, {
      font,
      size: 1,
      depth: 0.12,
      curveSegments: 8,
      bevelEnabled: true,
      bevelThickness: 0.02,
      bevelSize: 0.012,
      bevelSegments: 2
    })

    geometry.computeBoundingBox()
    geometry.userData.width = geometry.boundingBox.max.x - geometry.boundingBox.min.x
    geometry.center()
    geometries[letter] = geometry
    return geometries
  }, {})
}
